import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';

interface SettingsProps {
  onBack: () => void;
}

interface ChannelConfig {
  id: string;
  icon: string;
  iconUrl: string;
  name: string;
  setupType: 'deeplink' | 'token';
}

const channelConfigs: ChannelConfig[] = [
  { id: 'openclaw-weixin', icon: '💬', iconUrl: 'https://cdn.simpleicons.org/wechat/07c160', name: '微信', setupType: 'deeplink' },
  { id: 'telegram', icon: '✈️', iconUrl: 'https://cdn.simpleicons.org/telegram/26a5e4', name: 'Telegram', setupType: 'token' },
  { id: 'discord', icon: '🎮', iconUrl: 'https://cdn.simpleicons.org/discord/5865f2', name: 'Discord', setupType: 'token' },
  { id: 'feishu', icon: '🐦', iconUrl: 'https://www.feishu.cn/favicon.ico', name: '飞书', setupType: 'token' },
];

export default function Settings({ onBack }: SettingsProps) {
  const [aiName, setAiName] = useState(() => localStorage.getItem('ai_name') || 'AI超级男友');
  const [aiAvatar, setAiAvatar] = useState<string | null>(() => localStorage.getItem('ai_avatar'));
  const [aiSignature, setAiSignature] = useState('你的专属陪伴 💕');
  const [channels, setChannels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedChannel, setExpandedChannel] = useState<string | null>(null);
  const [tokenInput, setTokenInput] = useState('');
  const [appIdInput, setAppIdInput] = useState('');
  const [appSecretInput, setAppSecretInput] = useState('');
  const [wechatQrUrl, setWechatQrUrl] = useState('');
  const [wechatQrStatus, setWechatQrStatus] = useState('');
  const [wechatSessionId, setWechatSessionId] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadSettings();
    loadChannels();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadSettings = async () => {
    try {
      const { user } = await api.getMe();
      if (user) {
        setAiName(localStorage.getItem('ai_name') || 'AI超级男友');
        setAiSignature(localStorage.getItem('ai_signature') || '你的专属陪伴 💕');
        const av = localStorage.getItem('ai_avatar');
        if (av) setAiAvatar(av);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const loadChannels = async () => {
    try {
      const { channels: ch } = await api.getChannelStatus();
      setChannels(ch || []);
    } catch (err) {
      console.error('Failed to load channels:', err);
    }
  };

  const connectWeChat = async () => {
    setLoading(true);
    try {
      const result = await api.requestQRLogin('openclaw-weixin');
      if (result.qrUrl) {
        setWechatSessionId(result.sessionId || 'pending');
        setWechatQrStatus('pending');
        setExpandedChannel('openclaw-weixin');
        setWechatQrUrl(result.qrUrl);
        startPolling();
      }
    } catch (err) {
      console.error('WeChat connect failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(checkQrStatus, 3000);
  };

  const checkQrStatus = async () => {
    try {
      const result = await api.checkQRStatus('openclaw-weixin');
      setWechatQrStatus(result.status);
      if (result.status === 'confirmed') {
        if (pollRef.current) clearInterval(pollRef.current);
        await loadChannels();
        setWechatQrUrl('');
        setExpandedChannel(null);
      } else if (result.status === 'expired') {
        if (pollRef.current) clearInterval(pollRef.current);
      }
    } catch (err) {
      console.error('Check QR status failed:', err);
    }
  };

  const disconnectChannel = async (channel: string) => {
    try {
      await api.disconnectChannel(channel);
      await loadChannels();
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  const toggleExpand = (channelId: string) => {
    setExpandedChannel(expandedChannel === channelId ? null : channelId);
    setTokenInput('');
    setAppIdInput('');
    setAppSecretInput('');
  };

  const connectTokenChannel = async (channelId: string) => {
    if (!tokenInput.trim()) return;
    setLoading(true);
    try {
      await api.connectChannel(channelId, { token: tokenInput });
      await loadChannels();
      setExpandedChannel(null);
      setTokenInput('');
    } catch (err) {
      console.error('Connect failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectFeishu = async () => {
    if (!appIdInput.trim() || !appSecretInput.trim()) return;
    setLoading(true);
    try {
      await api.connectChannel('feishu', { appId: appIdInput, appSecret: appSecretInput } as any);
      await loadChannels();
      setExpandedChannel(null);
      setAppIdInput('');
      setAppSecretInput('');
    } catch (err) {
      console.error('Connect failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderExpandedInput = (config: ChannelConfig) => {
    if (config.id === 'openclaw-weixin' && wechatQrUrl) {
      const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(wechatQrUrl)}`;
      return (
        <div style={S.qrContainer}>
          <img src={qrImageUrl} alt="微信二维码" style={{ width: 200, height: 200, margin: '0 auto', display: 'block' }} />
          <div style={{ marginTop: 16, color: '#333', fontSize: 14, lineHeight: 2 }}>
            <div><strong>步骤 1：</strong>长按上方二维码 → 保存到手机</div>
            <div><strong>步骤 2：</strong>打开微信「扫一扫」→ 相册 → 选择图片</div>
          </div>
          <button onClick={() => { window.location.href = 'weixin://scanqrcode'; }} style={S.btnWechat}>
            打开微信扫一扫
          </button>
          {wechatQrStatus === 'pending' && (
            <button onClick={() => startPolling()} style={{ ...S.btn, ...S.btnPrimary, marginTop: 12 }}>已完成授权</button>
          )}
          {wechatQrStatus === 'scaned' && (
            <div style={S.qrStatus}>已扫码，等待确认...</div>
          )}
          {wechatQrStatus === 'confirmed' && (
            <div style={{ ...S.qrStatus, color: '#4ade80' }}>✅ 绑定成功！</div>
          )}
          {wechatQrStatus === 'expired' && (
            <>
              <div style={{ ...S.qrStatus, color: '#ef4444' }}>二维码已过期</div>
              <button onClick={connectWeChat} style={{ ...S.btn, ...S.btnPrimary, marginTop: 12 }}>重新生成</button>
            </>
          )}
        </div>
      );
    }

    if (config.id === 'feishu') {
      return (
        <div style={S.channelExpand}>
          <input style={S.inputField} placeholder="App ID" value={appIdInput} onChange={(e) => setAppIdInput(e.target.value)} />
          <input style={S.inputField} placeholder="App Secret" value={appSecretInput} onChange={(e) => setAppSecretInput(e.target.value)} />
          <div style={S.inputActions}>
            <button style={{ ...S.btn, ...S.btnSecondary }} onClick={() => toggleExpand(config.id)}>取消</button>
            <button style={{ ...S.btn, ...S.btnPrimary }} onClick={connectFeishu} disabled={loading}>确认</button>
          </div>
        </div>
      );
    }

    return (
      <div style={S.channelExpand}>
        <input style={S.inputField} placeholder="Bot Token" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} />
        <div style={S.inputActions}>
          <button style={{ ...S.btn, ...S.btnSecondary }} onClick={() => toggleExpand(config.id)}>取消</button>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => connectTokenChannel(config.id)} disabled={loading}>确认</button>
        </div>
      </div>
    );
  };

  const renderChannel = (config: ChannelConfig) => {
    const channel = channels.find((c) => c.id === config.id);
    const isConnected = channel?.status === 'connected';
    const isExpanded = expandedChannel === config.id;

    return (
      <div key={config.id} style={S.channelItem}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={S.channelInfo}>
              <img src={config.iconUrl} alt={config.name} style={{ width: 24, height: 24 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              <div>
                <div style={S.channelName}>{config.name}</div>
                <div style={S.channelStatus}>{isConnected ? '✅ 已连接' : '未连接'}</div>
              </div>
            </div>
            {isConnected ? (
              <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => disconnectChannel(config.id)}>解绑</button>
            ) : (
              <button
                style={{ ...S.btn, ...S.btnPrimary }}
                onClick={() => config.setupType === 'deeplink' ? connectWeChat() : toggleExpand(config.id)}
                disabled={loading}
              >
                {loading && isExpanded ? '连接中...' : '绑定'}
              </button>
            )}
          </div>
          {!isConnected && isExpanded && renderExpandedInput(config)}
        </div>
      </div>
    );
  };

  return (
    <div style={S.root}>
      <div style={S.container}>
        <div style={S.header}>
          <div style={S.avatarWrapper}>
            <div style={S.avatar}>
              {aiAvatar ? (
                <img src={aiAvatar} alt="AI头像" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span>💕</span>
              )}
            </div>
          </div>
          <div style={S.name}>{aiName}</div>
          <div style={S.onlineStatus}>在线</div>
        </div>

        <div style={S.section}>
          <div style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', fontSize: 14, padding: '8px 0' }}>
            {aiSignature || '...'}
          </div>
        </div>

        {/* Voice settings link */}
        <div style={S.section}>
          <div style={S.sectionTitle}>声音设置</div>
          <a
            href="/voice-settings.html"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 0', color: '#fafafa', textDecoration: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 20 }}>🎵</span>
              <span style={{ fontSize: 15 }}>自定义声音</span>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#71717a" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
          </a>
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>在这些地方也能找到我</div>
          {channelConfigs.map(renderChannel)}
        </div>

        <div style={S.upgradeSection}>
          <div style={S.planName}>当前套餐：免费版</div>
          <div style={S.comingSoon}>升级功能即将推出</div>
        </div>
      </div>

      <button style={S.backBtn} onClick={onBack}>返回聊天</button>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  root: {
    display: 'block', minHeight: '100vh',
    background: '#0a0a0f', color: '#fafafa',
    fontFamily: "-apple-system, 'SF Pro Display', 'Inter', 'PingFang SC', sans-serif",
  },
  container: { maxWidth: 600, margin: '0 auto', padding: '0 16px 80px' },
  header: { textAlign: 'center', padding: '40px 0 32px' },
  avatarWrapper: { position: 'relative', width: 120, height: 120, margin: '0 auto 20px' },
  avatar: {
    width: 120, height: 120, borderRadius: 30,
    background: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 48, overflow: 'hidden',
  },
  name: { fontSize: 24, fontWeight: 600, marginBottom: 8 },
  onlineStatus: { color: '#4ade80', fontSize: 14 },
  section: {
    background: '#18181b', border: '1px solid #27272a',
    borderRadius: 16, padding: 20, marginBottom: 16,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#a78bfa' },
  channelItem: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 0', borderBottom: '1px solid #27272a',
  },
  channelInfo: { display: 'flex', alignItems: 'center', gap: 12 },
  channelName: { fontSize: 15, fontWeight: 500 },
  channelStatus: { fontSize: 13, color: '#71717a', marginTop: 2 },
  channelExpand: { padding: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 8 },
  inputField: {
    width: '100%', padding: '10px 12px',
    background: '#0a0a0f', border: '1px solid #27272a',
    borderRadius: 8, color: '#fafafa', fontSize: 14,
    fontFamily: 'inherit', boxSizing: 'border-box',
  },
  inputActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  btn: {
    padding: '10px 20px', borderRadius: 12, border: 'none',
    fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s',
  },
  btnPrimary: { background: 'linear-gradient(135deg, #a78bfa, #7c3aed)', color: 'white' },
  btnSecondary: { background: '#27272a', color: '#fafafa' },
  btnDanger: { background: '#ef4444', color: 'white' },
  qrContainer: {
    marginTop: 16, padding: 20, background: 'white',
    borderRadius: 12, textAlign: 'center',
  },
  btnWechat: {
    width: '100%', padding: 14, background: '#07c160', color: 'white',
    border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600,
    cursor: 'pointer', marginTop: 16,
  },
  qrStatus: { marginTop: 12, fontSize: 14, color: '#71717a' },
  upgradeSection: {
    textAlign: 'center', padding: 24,
    background: 'linear-gradient(135deg, rgba(167,139,250,0.1), rgba(124,58,237,0.1))',
    borderRadius: 16, marginBottom: 16,
  },
  planName: { fontSize: 18, fontWeight: 600, marginBottom: 8 },
  comingSoon: { color: '#71717a', fontSize: 13 },
  backBtn: {
    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
    padding: '14px 32px', background: '#18181b', border: '1px solid #27272a',
    borderRadius: 24, color: '#fafafa', fontSize: 15, fontWeight: 500,
    cursor: 'pointer', zIndex: 10,
  },
};
